import React, { useState } from "react";
import GitHubIcon from "@mui/icons-material/GitHub";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";

import {
  accentText,
  mutedText,
  filledButton,
  outlineButton,
  Phone,
  TourBar,
  FeatureTiles,
  TourStep,
  InternalsGrid,
  ApkDownload,
  ApkNote,
  useActiveStep,
} from "../components/Walkthrough";

import appIcon from "../assets/icons/ShareInfo_AppIcon.png";
import trendingLight from "../assets/demo_screenshots/shareinfo/trending-light.webp";
import trendingDark from "../assets/demo_screenshots/shareinfo/trending-dark.webp";
import reportLight from "../assets/demo_screenshots/shareinfo/report-light.webp";
import reportDark from "../assets/demo_screenshots/shareinfo/report-dark.webp";
import sourcesLight from "../assets/demo_screenshots/shareinfo/sources-light.webp";
import sourcesDark from "../assets/demo_screenshots/shareinfo/sources-dark.webp";
import postsLight from "../assets/demo_screenshots/shareinfo/posts-light.webp";
import postsDark from "../assets/demo_screenshots/shareinfo/posts-dark.webp";
import searchLight from "../assets/demo_screenshots/shareinfo/search-light.webp";
import searchDark from "../assets/demo_screenshots/shareinfo/search-dark.webp";
import cryptoLight from "../assets/demo_screenshots/shareinfo/crypto-light.webp";
import cryptoDark from "../assets/demo_screenshots/shareinfo/crypto-dark.webp";
import topicLight from "../assets/demo_screenshots/shareinfo/topic-light.webp";
import topicDark from "../assets/demo_screenshots/shareinfo/topic-dark.webp";
import favoritesLight from "../assets/demo_screenshots/shareinfo/favorites-light.webp";
import favoritesDark from "../assets/demo_screenshots/shareinfo/favorites-dark.webp";
import followingLight from "../assets/demo_screenshots/shareinfo/following-light.webp";
import followingDark from "../assets/demo_screenshots/shareinfo/following-dark.webp";

const REPO = "ElAyachie/Share-Info";
const GITHUB_URL = `https://github.com/${REPO}`;
const APK_FILE = "ShareInfo.apk";
const MIN_ANDROID = "8.0";
const POSTER_URL = "https://drive.google.com/file/d/1Sb5iEn1b_WEMcOk3drJdf7zlmsTxr-Hz/view";

const shots = {
  trending: { light: trendingLight, dark: trendingDark },
  report: { light: reportLight, dark: reportDark },
  sources: { light: sourcesLight, dark: sourcesDark },
  posts: { light: postsLight, dark: postsDark },
  search: { light: searchLight, dark: searchDark },
  crypto: { light: cryptoLight, dark: cryptoDark },
  topic: { light: topicLight, dark: topicDark },
  favorites: { light: favoritesLight, dark: favoritesDark },
  following: { light: followingLight, dark: followingDark },
};

// The tour goes from what's being talked about, to one report in detail, to tracking your own
// topics. Each step is a section of the page with its own anchor, listed in the sticky bar.
const steps = [
  {
    id: "trending",
    label: "Trending",
    title: "See what people are talking about",
    body: "Share Info opens on the tickers with the most social activity right now, from StockTwits' trending list, plus the coins trending on CoinGecko. Each chip opens a sentiment report.",
    screens: [{ shot: shots.trending, caption: "Trending", alt: "The Trending tab with a row of ticker chips above the first ticker's sentiment report" }],
  },
  {
    id: "score",
    label: "Score",
    title: "Read the mood at a glance",
    body: "Every report leads with a score out of 100, where 50 is neutral, and a label from very bearish to very bullish. Under it: how many posts lean each way, how confident the reading is, a month of prices and a week of sentiment.",
    detail: "Price and mood often disagree, and seeing both together is the point.",
    screens: [{ shot: shots.report, caption: "Apple's report", alt: "Apple's report: a score out of 100 with its label, the bullish, neutral and bearish split, the price over the last month and sentiment by day" }],
  },
  {
    id: "sources",
    label: "Sources",
    title: "See where it comes from",
    body: "The score combines StockTwits, Bluesky, Reddit, YouTube and the news, and the report breaks it down by source. If one source can't be reached, the report says so and is built from the rest.",
    screens: [{ shot: shots.sources, caption: "By source", alt: "The sentiment trend and the per-source breakdown for StockTwits, Bluesky, Reddit and news" }],
  },
  {
    id: "posts",
    label: "Posts",
    title: "Read the posts behind it",
    body: "Below the summary is every post and headline that went into it, newest first, each with its own score. StockTwits authors can tag a post Bullish or Bearish, and that tag is shown and blended in. Tap a post to open the original.",
    screens: [{ shot: shots.posts, caption: "Latest posts", alt: "Scored StockTwits posts about Apple with Bullish and Bearish badges" }],
  },
  {
    id: "search",
    label: "Search",
    title: "Look up a stock or a coin",
    body: "Search finds stocks, funds and cryptocurrencies by name or ticker. Open a result to see its report, or tap the star to follow it.",
    screens: [
      { shot: shots.search, caption: "Search", alt: "Search results for bitcoin: Track bitcoin, then BTC, IBIT and BCH" },
      { shot: shots.crypto, caption: "Bitcoin", alt: "Bitcoin's report with its score, price and per-source breakdown" },
    ],
  },
  {
    id: "anything",
    label: "Anything",
    title: "Or follow anything at all",
    body: "Any search can also be tracked as a topic of its own, like Pokémon cards, a brand or a game. Topics without a ticker have no price or StockTwits stream, so the report is built from Bluesky, Reddit, YouTube and the news, and reads positive or negative instead of bullish or bearish.",
    screens: [{ shot: shots.topic, caption: "pokemon cards", alt: "The report for the topic pokemon cards, scored positive, from Bluesky, Reddit and news" }],
  },
  {
    id: "follow",
    label: "Follow",
    title: "Keep your own list",
    body: "Everything you follow appears on Favorites, one chip per topic. Following lists them for editing, next to the appearance setting and an explanation of how the score works.",
    screens: [
      { shot: shots.favorites, caption: "Favorites", alt: "The Favorites tab with chips for AAPL, BTC and pokemon cards above Apple's report" },
      { shot: shots.following, caption: "Following", alt: "The Following tab: appearance setting, followed topics, and how sentiment is scored" },
    ],
  },
  {
    id: "theme",
    label: "Theme",
    title: "Light or dark",
    body: "Following offers System, Light and Dark. Both themes use a palette checked for contrast, so scores and badges stay readable. Every screenshot on this page can be switched the same way with the App theme buttons.",
    // Always one of each, whatever the App theme buttons say.
    screens: [
      { shot: shots.report, theme: "light", caption: "Light", alt: "Apple's report in the light theme" },
      { shot: shots.report, theme: "dark", caption: "Dark", alt: "Apple's report in the dark theme" },
    ],
  },
];

const features = [
  { icon: TrendingUpIcon, title: "What's trending", text: "The tickers people are talking about now.", href: "#trending" },
  { icon: SpeedOutlinedIcon, title: "A score out of 100", text: "With a confidence level and a daily trend.", href: "#score" },
  { icon: ForumOutlinedIcon, title: "Five sources", text: "StockTwits, Bluesky, Reddit, YouTube and the news.", href: "#sources" },
  { icon: TravelExploreIcon, title: "Any topic", text: "Stocks, coins, or Pokémon cards.", href: "#anything" },
];

export const tech = ["Java", "Android SDK 36", "Material 3", "VADER sentiment", "Reddit & YouTube APIs", "Yahoo Finance"];

const internals = [
  {
    title: "Rebuilt in 2026",
    text: "Share Info started in 2021 as a college project built on Twitter, SocialGrep, Mediastack and a RapidAPI wrapper for Yahoo Finance. By 2026 those were shut down, paid, or exposing keys in the repository. The rebuild uses free sources, keeps the Reddit and YouTube credentials out of the repository, and moves to a Material 3 design and Android Gradle Plugin 8.13 with target SDK 36.",
  },
  {
    title: "VADER, ported to Java",
    text: "Each post is scored by a Java port of VADER, a sentiment model built for social media text. It handles negation, intensifiers, capitals, exclamation marks and \"but\" clauses. Unit tests check the port against the reference Python implementation on 82 real posts and edge cases.",
  },
  {
    title: "A market vocabulary",
    text: "A lexicon on top of VADER adds trading slang (\"bagholder\", \"rekt\"), emoji such as 🚀 and 📉, and phrases like \"all-time high\". It also neutralizes words that are emotional elsewhere but not in finance, like \"shares\" and \"interest\", and ignores the topic's own name, so \"Best Buy\" doesn't make every post about it positive.",
  },
  {
    title: "One score from many posts",
    text: "Recent posts count more, with a two-day half-life, and engagement counts logarithmically, so one viral post can't outweigh everyone else. The average is pulled toward neutral when there are only a few posts, and confidence comes from the effective sample size.",
  },
  {
    title: "Sources that fail gracefully",
    text: "Sources are fetched in parallel with timeouts, and one that fails is reported instead of failing the whole report. Reddit and YouTube go through their official APIs, with credentials kept out of the repository. Without a Reddit key the app falls back to Reddit's public feed, which allows one request a minute, and paces its calls to match.",
  },
  {
    title: "Tested against real responses",
    text: "JVM tests parse responses captured from each service, exercise the scoring and weighting, and build reports from fake sources that succeed, fail or time out.",
  },
];

const stepIds = steps.map((s) => s.id);

function ShareInfoProject() {
  const [appTheme, setAppTheme] = useState(null);
  const active = useActiveStep(stepIds);

  return (
    <div className="w-full text-[black] dark:text-[white] pb-16">
      {/* Hero: text and two phones side by side on large screens, stacked on phones */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-8 lg:pt-14 flex flex-col lg:flex-row items-center gap-10 lg:gap-12">
        <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left">
          <img src={appIcon} alt="" aria-hidden="true" width="192" height="192" className="w-20 h-20 lg:w-24 lg:h-24 drop-shadow-md" />
          <p className={`pt-4 text-sm font-semibold tracking-widest uppercase ${accentText}`}>Android app</p>
          <h1 className="text-[42px] sm:text-5xl lg:text-[56px] leading-tight font-bold pb-3">Share Info</h1>
          <p className="text-xl lg:text-[22px] leading-relaxed text-[#333] dark:text-[#d6d9e3]">
            How are people talking about a stock, a coin, or anything else? Share Info reads StockTwits,
            Bluesky, Reddit, YouTube and the news, and turns the posts into one sentiment score.
          </p>
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-6">
            <a href={GITHUB_URL} target="_blank" rel="noreferrer" className={filledButton}>
              <GitHubIcon fontSize="small" aria-hidden="true" />
              Source code
            </a>
            <ApkDownload repo={REPO} file={APK_FILE} />
            <a href="#built" className={outlineButton}>
              How it works
            </a>
          </div>
          <ApkNote repo={REPO} file={APK_FILE} minAndroid={MIN_ANDROID} className="pt-3" />
          <ul className="flex flex-wrap justify-center lg:justify-start gap-2 pt-6" aria-label="Built with">
            {tech.map((t) => (
              <li key={t} className="rounded-full border border-[#eb4d5c] dark:border-[#c9a7ff] px-3 py-0.5 text-sm font-medium text-[#eb4d5c] dark:text-[#c9a7ff]">
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-start justify-center gap-4 lg:gap-6">
          <Phone screen={{ shot: shots.report, alt: "Share Info sentiment report for Apple" }} appTheme={appTheme} size="w-[40vw] max-w-[210px] lg:w-[220px]" />
          <div className="pt-10 lg:pt-14">
            <Phone screen={{ shot: shots.topic, alt: "Share Info report for the topic pokemon cards" }} appTheme={appTheme} size="w-[40vw] max-w-[210px] lg:w-[220px]" />
          </div>
        </div>
      </section>

      {/* What it does, at a glance. Each tile jumps to its part of the tour. */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-14">
        <FeatureTiles features={features} />
      </section>

      {/* The tour */}
      <section className="w-full pt-16" aria-labelledby="tour-heading">
        <div className="w-full max-w-5xl mx-auto px-4">
          <h2 id="tour-heading" className="text-4xl lg:text-5xl font-bold pb-2 text-center lg:text-left">Take the tour</h2>
          <p className="text-lg lg:text-xl text-center lg:text-left pb-6">
            From what everyone is talking about, to one report in detail, to a list of your own.
          </p>
        </div>

        <TourBar steps={steps} active={active} appTheme={appTheme} setAppTheme={setAppTheme} />

        <ol className="w-full max-w-5xl mx-auto px-4">
          {steps.map((step, i) => (
            <TourStep key={step.id} step={step} index={i} total={steps.length} appTheme={appTheme} />
          ))}
        </ol>
      </section>

      {/* Engineering notes for readers who want to know how it works */}
      <section id="built" className="scroll-mt-6 w-full max-w-5xl mx-auto px-4 pt-10" aria-labelledby="built-heading">
        <h2 id="built-heading" className="text-4xl lg:text-5xl font-bold pb-2 text-center lg:text-left">How it's built</h2>
        <p className="text-lg lg:text-xl text-center lg:text-left pb-7">
          How a feed of posts becomes one score you can trust.
        </p>
        <InternalsGrid items={internals} />
        <div className="flex flex-wrap items-center justify-center gap-3 pt-10">
          <a href={GITHUB_URL} target="_blank" rel="noreferrer" className={filledButton}>
            <GitHubIcon fontSize="small" aria-hidden="true" />
            Read the code
          </a>
          <ApkDownload repo={REPO} file={APK_FILE} />
          <a href={POSTER_URL} target="_blank" rel="noreferrer" className={outlineButton}>
            The original 2021 poster
          </a>
        </div>
        <p className={`pt-8 text-center ${mutedText}`}>
          Created by Mohamed Oulal with Caleb Franco, under the guidance of Professor Chen-Hsiang Yu, Ph.D., at
          Wentworth Institute of Technology. Rebuilt by Mohamed Oulal in 2026.
        </p>
        <p className={`pt-2 text-center text-sm ${mutedText}`}>Sentiment scores are not financial advice.</p>
      </section>
    </div>
  );
}

export default ShareInfoProject;
